import { initializeApp, getApps, getApp } from 'firebase/app';
import { initializeAnalytics, isSupported, logEvent } from 'firebase/analytics';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

const ANALYTICS_HOSTS = new Set(['nativenest.in', 'www.nativenest.in']);
const PAGE_VIEW_TIMEOUT_MS = 2000;
const APPS_PATH = '/apps';
const APPS_PAGE_EVENT = 'apps_page_view';
const APPS_PAGE_TITLE = 'Apps | Native Nest';

function canonicalPagePath(path) {
  if (!path) return path;
  return path.split('?')[0] === APPS_PATH ? APPS_PATH : path;
}

function isAppsPath(path) {
  return canonicalPagePath(path) === APPS_PATH;
}

let analyticsPromise;
let previousPageLocation = typeof document !== 'undefined' ? document.referrer : '';
let currentPageView = { path: null, promise: null };

function hasFirebaseConfig() {
  return Boolean(
    firebaseConfig.apiKey &&
    firebaseConfig.appId &&
    firebaseConfig.measurementId
  );
}

function shouldEnableAnalytics() {
  if (typeof window === 'undefined') return false;
  if (process.env.NODE_ENV !== 'production') return false;
  if (!hasFirebaseConfig()) return false;
  return ANALYTICS_HOSTS.has(window.location.hostname);
}

function getFirebaseApp() {
  return getApps().length ? getApp() : initializeApp(firebaseConfig);
}

export function initAnalytics() {
  if (!analyticsPromise) {
    analyticsPromise = (async () => {
      try {
        if (!shouldEnableAnalytics()) return null;
        const supported = await isSupported();
        if (!supported) return null;

        return initializeAnalytics(getFirebaseApp(), {
          config: { send_page_view: false },
        });
      } catch (error) {
        console.warn('Firebase Analytics init failed', error);
        return null;
      }
    })();
  }

  return analyticsPromise;
}

async function dispatchPageView(path) {
  const analytics = await initAnalytics();
  if (!analytics) return;

  const pageLocation = window.location.href;
  if (isAppsPath(path)) {
    document.title = APPS_PAGE_TITLE;
  }

  await new Promise((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      resolve();
    };

    logEvent(analytics, 'page_view', {
      page_path: path,
      page_location: pageLocation,
      page_title: document.title,
      page_referrer: previousPageLocation || undefined,
      transport_type: 'beacon',
      event_callback: finish,
      event_timeout: PAGE_VIEW_TIMEOUT_MS,
    });

    if (isAppsPath(path)) {
      logEvent(analytics, APPS_PAGE_EVENT, {
        page_path: APPS_PATH,
        page_location: pageLocation,
        page_title: APPS_PAGE_TITLE,
        transport_type: 'beacon',
      });
    }

    setTimeout(finish, PAGE_VIEW_TIMEOUT_MS);
  });

  previousPageLocation = pageLocation;
}

export function logPageView(path) {
  const canonicalPath = canonicalPagePath(path);
  if (!canonicalPath) return Promise.resolve();
  if (currentPageView.path === canonicalPath) return currentPageView.promise;

  currentPageView = {
    path: canonicalPath,
    promise: dispatchPageView(canonicalPath),
  };

  return currentPageView.promise;
}
